class MCartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();
      const cartItems = this.closest("m-cart") || this.closest("m-cart-drawer-items");
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define("m-cart-remove-button", MCartRemoveButton);

const CART_PAGE_TIER1_CENTS = 1000 * 100;
const CART_PAGE_TIER2_CENTS = 3000 * 100;
const CART_PAGE_GIFT_VARIANTS = {
  STUD_EARRINGS: 52066849915180,
  TENNIS_CHAIN: 52066127839532,
};
const CART_PAGE_MANAGED_GIFT_VARIANT_IDS = new Set([
  CART_PAGE_GIFT_VARIANTS.STUD_EARRINGS,
  CART_PAGE_GIFT_VARIANTS.TENNIS_CHAIN,
]);

class MCartTemplate extends HTMLElement {
  constructor() {
    super();
    this.cartUpdateUnsubscriber = undefined;
    this.syncingCartPageGifts = false;
    this.isCartPage = MinimogSettings.templateName === "cart";
    this.cartDrawerWrapper = document.querySelector("m-cart-drawer");
    this.cartDrawerInner = document.querySelector(".m-cart-drawer__inner");
    this.mainCartItems = this.querySelector("[data-minimog-cart-items]");
    this.cartSubTotal = this.querySelector("[data-cart-subtotal]");
    this.cartDiscount = this.querySelector("[data-minimog-cart-discounts]");
    this.giftWrapping = this.querySelector("[data-minimog-gift-wrapping]");
    let loadingTarget = this.cartDrawerInner;
    if (this.isCartPage) loadingTarget = document.body;
    this.loading = new MinimogLibs.AnimateLoading(loadingTarget, { overlay: loadingTarget });

    this.rootUrl = window.Shopify.routes.root;

    const debouncedOnChange = debounce((event) => {
      if (event.target.name !== "id") {
        this.onChange(event);
      }
    }, 300);
    if (this.isCartPage) {
      this.mainCartItems.addEventListener("change", debouncedOnChange.bind(this));
    } else {
      this.addEventListener("change", debouncedOnChange.bind(this));
    }

    MinimogEvents.subscribe(MinimogTheme.pubSubEvents.cartUpdate, (response) => {
      this.getCart().then((cart) => {
        this.updateCartCount(cart.item_count);
      });
    });

    if (this.isCartPage) {
      // Keep tier gifts normalized when cart page is loaded directly.
      setTimeout(() => {
        this.ensureCartPageTierGifts();
      }, 250);
    }
  }

  updateCartCount(itemCount) {
    const cartCounts = document.querySelectorAll(".m-cart-count-bubble");
    cartCounts.forEach((cartCount) => {
      if (itemCount > 0) {
        cartCount.textContent = itemCount;
        cartCount.classList.remove("m:hidden");
      } else {
        cartCount.classList.add("m:hidden");
      }
    });
  }

  getCart() {
    return fetchJSON(this.rootUrl + "cart.json");
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = MinimogEvents.subscribe(MinimogTheme.pubSubEvents.cartUpdate, (event) => {
      if (event.source === "main-cart-items") {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onCartUpdate(renderFooter = true) {
    const { routes } = window.MinimogSettings;
    fetch(`${routes.cart}?section_id=cart-template`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, "text/html");
        const cartItems = html.querySelector("[data-minimog-cart-items]");
        const cartSubTotal = html.querySelector("[data-cart-subtotal]");
        const cartDiscount = html.querySelector("[data-minimog-cart-discounts]");
        const giftWrapping = html.querySelector("[data-minimog-gift-wrapping]");
        if (this.isCartPage) {
          this.mainCartItems.innerHTML = cartItems.innerHTML;
          if (renderFooter) {
            this.cartSubTotal.innerHTML = cartSubTotal.innerHTML;
            this.cartDiscount.innerHTML = cartDiscount.innerHTML;
            this.giftWrapping.innerHTML = giftWrapping.innerHTML;
          }
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  onChange(event) {
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute("name"));
  }

  isFreeGiftItem(lineItem) {
    if (!lineItem) return false;

    const lineProperties = lineItem.properties || {};
    const hasFreeGiftProperty = Object.keys(lineProperties).some((key) => {
      const normalizedKey = String(key || "").toLowerCase();
      if (!["free_gift", "free gift", "_free_gift", "hidden_gift", "gift"].includes(normalizedKey)) return false;
      const value = String(lineProperties[key] || "").toLowerCase();
      return value === "true" || value === "yes" || value === "1";
    });

    return (
      CART_PAGE_MANAGED_GIFT_VARIANT_IDS.has(lineItem.variant_id) ||
      hasFreeGiftProperty ||
      lineItem.final_price === 0
    );
  }

  getDesiredTierGiftVariantIds(eligibleTotalCents) {
    if (eligibleTotalCents >= CART_PAGE_TIER2_CENTS) {
      return [CART_PAGE_GIFT_VARIANTS.STUD_EARRINGS, CART_PAGE_GIFT_VARIANTS.TENNIS_CHAIN];
    }
    if (eligibleTotalCents >= CART_PAGE_TIER1_CENTS) {
      return [CART_PAGE_GIFT_VARIANTS.STUD_EARRINGS];
    }
    return [];
  }

  getEligibleSubtotalCents(items = []) {
    return items.reduce((subtotal, item) => {
      if (CART_PAGE_MANAGED_GIFT_VARIANT_IDS.has(item.variant_id)) return subtotal;
      return subtotal + (item.final_line_price || 0);
    }, 0);
  }

  async addMissingGiftVariants(variantIds = []) {
    if (!variantIds.length) return;
    await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        items: variantIds.map((id) => ({ id, quantity: 1 })),
      }),
    });
  }

  async syncCartPageTierGifts(cartState) {
    if (!this.isCartPage || !cartState || !Array.isArray(cartState.items) || this.syncingCartPageGifts) {
      return { changed: false, cart: cartState };
    }

    this.syncingCartPageGifts = true;
    try {
      let workingCart = cartState;
      const eligibleSubtotalCents = this.getEligibleSubtotalCents(workingCart.items);
      const desiredGiftVariantIds = this.getDesiredTierGiftVariantIds(eligibleSubtotalCents);
      const desiredGiftSet = new Set(desiredGiftVariantIds);

      const giftLines = workingCart.items.filter((item) => CART_PAGE_MANAGED_GIFT_VARIANT_IDS.has(item.variant_id));
      const seenDesiredGift = new Set();
      const updates = {};

      giftLines.forEach((giftLine) => {
        const isDesiredGift = desiredGiftSet.has(giftLine.variant_id);
        if (!isDesiredGift) {
          updates[giftLine.key] = 0;
          return;
        }

        if (seenDesiredGift.has(giftLine.variant_id)) {
          updates[giftLine.key] = 0;
          return;
        }

        seenDesiredGift.add(giftLine.variant_id);
        if (giftLine.quantity !== 1) {
          updates[giftLine.key] = 1;
        }
      });

      let changed = false;

      if (Object.keys(updates).length) {
        await fetch(`${globalThis.MinimogSettings.routes.cart_update_url}`, {
          ...fetchConfig(),
          body: JSON.stringify({ updates }),
        });
        workingCart = await this.getCart();
        changed = true;
      }

      const presentGiftIds = new Set(
        (workingCart.items || [])
          .filter((item) => CART_PAGE_MANAGED_GIFT_VARIANT_IDS.has(item.variant_id))
          .map((item) => item.variant_id)
      );
      const missingGiftIds = desiredGiftVariantIds.filter((id) => !presentGiftIds.has(id));

      if (missingGiftIds.length) {
        await this.addMissingGiftVariants(missingGiftIds);
        workingCart = await this.getCart();
        changed = true;
      }

      return { changed, cart: workingCart };
    } finally {
      this.syncingCartPageGifts = false;
    }
  }

  async ensureCartPageTierGifts() {
    if (!this.isCartPage || this.syncingCartPageGifts) return;
    const cart = await this.getCart();
    const syncResult = await this.syncCartPageTierGifts(cart);
    if (!syncResult.changed) return;

    this.onCartUpdate(true);
    this.classList.toggle("m-cart--empty", syncResult.cart.item_count === 0);
    if (this.cartDrawerWrapper) {
      this.cartDrawerWrapper.classList.toggle("m-cart--empty", syncResult.cart.item_count === 0);
    }
    MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, {
      ...syncResult.cart,
      source: "main-cart-items",
    });
  }

  updateQuantity(line, quantity, name) {
    this.loading.start();
    const { routes } = window.MinimogSettings;

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body })
      .then((response) => {
        return response.text();
      })
      .then(async (state) => {
        const parsedState = JSON.parse(state);
        let quantityElement = document.getElementById(`MinimogDrawer-quantity-${line}`);
        if (this.isCartPage) {
          if (MinimogTheme.config.mqlMobile) {
            quantityElement = document.querySelector(`.MinimogQuantity-${line}.MinimogQuantity-mobile`);
          } else {
            quantityElement = document.querySelector(`.MinimogQuantity-${line}.MinimogQuantity-desktop`);
          }
        }
        const items = document.querySelectorAll(".m-cart-item");

        if (parsedState.errors) {
          this.loading.finish();
          quantityElement.value = quantityElement.getAttribute("value");
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        const giftSyncResult = await this.syncCartPageTierGifts(parsedState);
        if (giftSyncResult.changed) {
          this.onCartUpdate(true);
          this.classList.toggle("m-cart--empty", giftSyncResult.cart.item_count === 0);
          if (this.cartDrawerWrapper) {
            this.cartDrawerWrapper.classList.toggle("m-cart--empty", giftSyncResult.cart.item_count === 0);
          }
          MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, {
            ...giftSyncResult.cart,
            source: "main-cart-items",
          });
          return;
        }

        this.classList.toggle("m-cart--empty", parsedState.item_count === 0);
        if (this.cartDrawerWrapper)
          this.cartDrawerWrapper.classList.toggle("m-cart--empty", parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });

        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = "";
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === "undefined") {
            message = window.MinimogStrings.cartError;
          } else {
            message = window.MinimogStrings.quantityError.replace("{{ quantity }}", updatedValue);
          }
        }
        this.updateLiveRegions(line, message);
        MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, { ...parsedState, source: "main-cart-items" });
      })
      .catch(() => { })
      .finally(() => {
        this.loading.finish();
      });
  }

  updateLiveRegions(line, message) {
    let lineItemNode = document.getElementById(`MinimogCartDrawer-Item-${line}`);
    if (this.isCartPage) lineItemNode = document.getElementById(`MinimogCart-Item-${line}`);
    if (message !== "" && lineItemNode) {
      MinimogTheme.Notification.show({
        target: lineItemNode,
        type: "warning",
        message: message,
      });
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "MinimogCart",
        section: "cart-template",
        selector: "[data-minimog-cart-items]",
      },
      {
        id: "MinimogCart",
        section: "cart-template",
        selector: "[data-cart-subtotal]",
      },
      {
        id: "MinimogCart",
        section: "cart-template",
        selector: "[data-minimog-cart-discounts]",
      },
      {
        id: "MinimogCart",
        section: "cart-template",
        selector: "[data-minimog-gift-wrapping]",
      },
    ];
  }
}

customElements.define("m-cart", MCartTemplate);
